import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IoMdSettings } from "react-icons/io";
import { IoIosArrowBack } from "react-icons/io";

import { COURSE_DOCUMENT_FILE_STATUS, routes } from "consts";
import { useCourse, useCourseStudentsDocuments } from "api/hooks";
import { PaziaButton, PaziaLoader } from "components/atoms";
import { appendFileToBrowser } from "utils";
import { CourseStudentDocument, StudentDocumentFile, courseStudentsApi } from "api/courseStudentsApi";
import { DownloadIcon, RefreshIcon } from "components/atoms/Icons";
import { DownloadCourseDocumentsModal, GenerateCourseDocuments } from "components/templates";
import { TanTableColumn } from "components/molecules";
import { TanTableClient } from "components/molecules";
import { EditCourseDocuments } from "./EditCourseDocuments";

export const CourseDocumentsPage = () => {
  const { courseId } = useParams();
  const [editMode, setEditMode] = useState(false);
  const { data, loading } = useCourse(Number(courseId));

  if (loading) return <PaziaLoader position="center" />;
  if (!data) return null;

  return (
    <div>
      <DocumentsHeader courseId={Number(courseId)} editMode={editMode} onClick={() => setEditMode((pv) => !pv)} />
      {editMode ? (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50/30 p-4 dark:border-amber-700 dark:bg-amber-900/10">
          <EditCourseDocuments courseId={Number(courseId)} />
        </div>
      ) : (
        <DocumentsPreview courseId={Number(courseId)} />
      )}
    </div>
  );
};

const DocumentsPreview = ({ courseId }: { courseId: number }) => {
  const [selectedStudents, setSelectedStudents] = useState<CourseStudentDocument[]>([]);
  const [showGenerateView, setShowGenerateView] = React.useState(false);
  const [isDownloadModalOpen, setIsDownloadModalOpen] = React.useState(false);
  const { documents, refetch, isLoading } = useCourseStudentsDocuments(Number(courseId));
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [pendingSelection, setPendingSelection] = useState<string[] | null>(null);

  const handleSelectedItems = useCallback(
    (selectedItems: string[]) => {
      // If documents are still loading or haven't loaded yet, store the selection for later
      if (isLoading || documents.length === 0) {
        setPendingSelection(selectedItems);
        return;
      }

      const filteredStudents = documents.filter((doc) => selectedItems.includes(doc.id.toString()));
      setSelectedStudents(filteredStudents);
      setSelectedItems([...selectedItems]);
      setPendingSelection(null); // Clear any pending selection
    },
    [documents, isLoading],
  );

  // Apply pending selection once documents are loaded
  useEffect(() => {
    if (!isLoading && documents.length > 0 && pendingSelection !== null) {
      handleSelectedItems(pendingSelection);
    }
  }, [isLoading, documents, pendingSelection, handleSelectedItems]);

  const columns: TanTableColumn<CourseStudentDocument>[] = useMemo(() => {
    return [
      {
        id: "id",
        accessorKey: "id",
        header: "ID",
        width: "auto",
        enableColumnFilter: false,
        cell: (info) => info.getValue(),
      },
      {
        id: "firstName",
        accessorKey: "firstName",
        header: "Imię i Nazwisko",
        enableColumnFilter: false,
        cell: (info) => info.row.original.firstName + " " + info.row.original.surname,
      },
      {
        id: "email",
        accessorKey: "email",
        header: "Email",
        enableColumnFilter: false,
      },
    ];
  }, []);
  const tableColumns = useMemo(() => {
    const allDocuments = documents.flatMap((doc) => doc.documents);
    const uniqueDocumentIds = [...new Set(allDocuments.map((doc) => doc.courseDocumentId))];
    const uniqDocuments: StudentDocumentFile[] = uniqueDocumentIds
      .map((id) => allDocuments.find((doc) => doc.courseDocumentId === id)!)
      .filter((x) => x != undefined);
    const documencolumns: TanTableColumn<CourseStudentDocument>[] = uniqDocuments.map((doc: StudentDocumentFile) => ({
      id: doc.courseDocumentId.toString(),
      accessorKey: doc.courseDocumentId.toString(),
      header: doc.documentName,
      enableColumnFilter: false,
      cell: (info) => {
        const document = info.row.original.documents.find((d) => d.courseDocumentId === doc?.courseDocumentId);
        if (!document) return <span>Brak</span>;

        const download = async (e: React.MouseEvent<SVGSVGElement>) => {
          e.stopPropagation();
          const response = await courseStudentsApi.download(Number(courseId), document.courseDocumentFileId);
          appendFileToBrowser(response.data, document.fileName);
        };
        if (document.status == COURSE_DOCUMENT_FILE_STATUS.SuccessfullyGenerated)
          return <DownloadIcon onClick={download} className="cursor-pointer hover:text-slate-500" />;
        if (document.status == COURSE_DOCUMENT_FILE_STATUS.Failed)
          return (
            <span className="cursor-help text-red-500" title={document.errorMessage ?? "Nieznany błąd"}>
              błąd generowania
            </span>
          );
        if (document.status == COURSE_DOCUMENT_FILE_STATUS.Generating)
          return <span className="text-primary-500 animate-pulse">Tworzenie...</span>;

        return null;
      },
    }));
    console.table(documencolumns);
    return [...columns, ...documencolumns];
  }, [documents]);
  // Show generate view instead of main table
  if (showGenerateView) {
    return (
      <GenerateCourseDocuments
        courseId={Number(courseId)}
        students={selectedStudents}
        onBack={(refreshTable) => {
          setShowGenerateView(false);
          if (refreshTable) refetch();
        }}
      />
    );
  }

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="mb-2 mt-4 text-lg font-semibold">Lista kursantów</div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="cursor-pointer" onClick={() => refetch()}>
            <RefreshIcon />
          </div>
          <PaziaButton
            type="success"
            title="Pobierz dokumenty"
            disabled={selectedItems.length < 1}
            onClick={() => setIsDownloadModalOpen(true)}
          />
          <PaziaButton
            type="primary"
            title="Generuj"
            disabled={selectedItems.length < 1}
            onClick={() => setShowGenerateView(true)}
          />
        </div>
      </div>
      <TanTableClient
        data={documents}
        columns={tableColumns}
        hideBorder
        onSelectionChange={(x) => handleSelectedItems(x.map((y) => y.toString()))}
        selectOnClick={true}
      />

      <DownloadCourseDocumentsModal
        courseId={Number(courseId)}
        isOpen={isDownloadModalOpen}
        students={selectedStudents}
        onCloseRequest={(refreshTable) => {
          setIsDownloadModalOpen(false);
          if (refreshTable) refetch();
        }}
      />
    </>
  );
};

const DocumentsHeader = ({
  courseId,
  editMode,
  onClick,
}: {
  courseId: number;
  editMode: boolean;
  onClick: () => void;
}) => {
  if (editMode) {
    return (
      <div className="pt-4">
        <div className="rounded-lg border-2 border-amber-300 bg-amber-50 p-4 shadow-sm dark:border-amber-600 dark:bg-amber-900/20">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="mb-2 flex items-center gap-2">
                <div className="rounded-md bg-amber-200 px-2 py-1 text-sm font-semibold text-amber-800 dark:bg-amber-700 dark:text-amber-200">
                  TRYB EDYCJI
                </div>
              </div>
              <div className="text-sm text-amber-700 dark:text-amber-300">
                <strong>Edytujesz szablon dokumentów przypisanych do kursu.</strong> <br />
                W tym trybie możesz dodawać, usuwać i modyfikować szablony dokumentów. <br />
                Zmiany wpłyną na wszystkie nowo generowane dokumenty dla tego kursu. <br />
                <span className="mt-1 inline-flex items-center gap-1 text-xs">
                  Kliknij <IoIosArrowBack className="inline" size={14} /> <strong>Powrót</strong>, aby zakończyć edycję
                  i wrócić do listy dokumentów.
                </span>
              </div>
            </div>
            <div
              className="ml-4 flex flex-shrink-0 cursor-pointer items-center gap-2 rounded-md bg-white px-3 py-2 text-amber-700 shadow-sm transition-all duration-200 hover:bg-amber-100 hover:text-amber-800 dark:bg-amber-800/50 dark:text-amber-200 dark:hover:bg-amber-700/50"
              onClick={onClick}
            >
              <IoIosArrowBack size={20} />
              <span className="font-medium">Powrót</span>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start justify-between pt-4">
      <div className="flex-1 text-sm text-zinc-500 dark:text-zinc-400">
        <div className="mb-2">
          <span className="rounded-md bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-200">
            WIDOK LISTY
          </span>
        </div>
        Dokumenty są tworzone na podstawie szablonów dokumentów przypisanych do kursu. <br />
        Kliknij koło zębate <IoMdSettings className="inline" />, aby przejść do edycji szablonu dokumentu w kursie.{" "}
        <br />
      </div>
      <div className="ml-4 flex-shrink-0">
        <IoMdSettings
          onClick={onClick}
          className="hover:text-primary-600 dark:hover:text-primary-400 cursor-pointer text-zinc-600 transition-colors duration-200 dark:text-zinc-300"
          size={28}
          title="Edytuj szablon dokumentów"
        />
      </div>
    </div>
  );
};
